// Shared signature-count math so the person card shows the SAME number of "signature" dots whether it's
// opened from the Board or the Anesthesiologists tab. A signature = the anesthesiologist's sign-off on a
// room: a SOLO room (no CRNA/student) counts 1; each DISTINCT CRNA/student supervised counts 1 (the same
// nurse following the doctor across sequential rooms collapses to 1). Cases are grouped by PHYSICAL ROOM
// first so many sequential cases in one room count as one room, not one-per-case.
//
// The Board (which applies its room-transfer overlay via `doctorIdsOf`) and the Anesthesiologists tab
// (raw allocations) both call this with their own resolvers, so the count is identical for the same data.

export interface SigAllocation {
  operatingRoomScheduleId: string;
  allocatedDoctorIds?: string[];
  allocatedNurseIds?: string[];
}

export function signaturesUsed(
  doctorId: string,
  allocations: SigAllocation[],
  opts: {
    roomIdOf: (scheduleId: string) => string | undefined;   // schedule -> physical room (group cases by room)
    isNurseLike: (nurseId: string) => boolean;              // true for CRNA or student ids
    doctorIdsOf?: (a: SigAllocation) => string[];           // defaults to a.allocatedDoctorIds (Board passes effective ids)
    nurseIdsOf?: (a: SigAllocation) => string[];            // defaults to a.allocatedNurseIds (Board passes nurse-transfer-effective ids)
  },
): number {
  const nurseIdsByRoom = new Map<string, string[]>();
  for (const a of allocations) {
    const dids = opts.doctorIdsOf ? opts.doctorIdsOf(a) : (a.allocatedDoctorIds || []);
    if (!dids.includes(doctorId)) continue;
    // Group by room; if the schedule can't be resolved, fall back to the schedule id so an orphan case
    // still counts as its own room rather than silently vanishing.
    const roomKey = opts.roomIdOf(a.operatingRoomScheduleId) || a.operatingRoomScheduleId;
    const ns = nurseIdsByRoom.get(roomKey) || [];
    (opts.nurseIdsOf ? opts.nurseIdsOf(a) : (a.allocatedNurseIds || [])).forEach(id => ns.push(id));
    nurseIdsByRoom.set(roomKey, ns);
  }
  let soloRooms = 0;
  const distinctSupervisees = new Set<string>();
  for (const ns of nurseIdsByRoom.values()) {
    const supervisees = ns.filter(opts.isNurseLike);
    if (supervisees.length === 0) soloRooms++;
    else supervisees.forEach(id => distinctSupervisees.add(id));
  }
  return soloRooms + distinctSupervisees.size;
}

// A doctor's rooms for the "at the moment" metrics: their cases grouped by PHYSICAL room, so a room holding
// many sequential cases is one room with the earliest start, the latest end, and the union of its supervisees.
export interface DoctorRoom { startMs: number; endMs: number; nurseIds: Set<string> }

export interface ConcurrencyOpts {
  roomIdOf: (scheduleId: string) => string | undefined;   // schedule -> physical room (group cases by room)
  startMsOf: (scheduleId: string) => number | undefined;  // schedule -> start epoch ms
  endMsOf: (scheduleId: string) => number | undefined;    // schedule -> end epoch ms
  isNurseLike: (nurseId: string) => boolean;
  doctorIdsOf?: (a: SigAllocation) => string[];           // Board passes its room-transfer-effective ids
  nurseIdsOf?: (a: SigAllocation) => string[];            // Board passes its nurse-transfer-effective ids
  windowMs?: number;                                      // "within the hour" lookahead; defaults to 60 min
  // Rooms not (yet) in `allocations` — the drop handler uses this to include the room being placed, whose
  // pending nurse list only exists in the in-flight payload.
  extraRooms?: { roomId: string; startMs: number; endMs: number; nurseIds: string[] }[];
}

export function doctorRoomsFor(doctorId: string, allocations: SigAllocation[], opts: ConcurrencyOpts): DoctorRoom[] {
  const rooms = new Map<string, DoctorRoom>();
  const add = (roomKey: string, s: number, e: number, nurseIds: string[]) => {
    if (!Number.isFinite(s) || !Number.isFinite(e)) return;
    let r = rooms.get(roomKey);
    if (!r) { r = { startMs: s, endMs: e, nurseIds: new Set<string>() }; rooms.set(roomKey, r); }
    else { r.startMs = Math.min(r.startMs, s); r.endMs = Math.max(r.endMs, e); }
    nurseIds.filter(opts.isNurseLike).forEach(id => r!.nurseIds.add(id));
  };
  for (const a of allocations) {
    const dids = opts.doctorIdsOf ? opts.doctorIdsOf(a) : (a.allocatedDoctorIds || []);
    if (!dids.includes(doctorId)) continue;
    const s = opts.startMsOf(a.operatingRoomScheduleId);
    const e = opts.endMsOf(a.operatingRoomScheduleId);
    if (s === undefined || e === undefined) continue;
    // If the schedule can't be resolved to a room, fall back to the schedule id so an orphan case still
    // counts as its own room rather than silently vanishing.
    add(opts.roomIdOf(a.operatingRoomScheduleId) || a.operatingRoomScheduleId, s, e, opts.nurseIdsOf ? opts.nurseIdsOf(a) : (a.allocatedNurseIds || []));
  }
  for (const x of opts.extraRooms || []) add(x.roomId, x.startMs, x.endMs, x.nurseIds);
  return [...rooms.values()];
}

// Signatures the doctor is carrying AT ONE INSTANT: the rooms going now (endMs > moment) or starting within
// the hour. Each solo room is 1; each distinct CRNA/student supervised is 1 (same nurse in two rooms = 1).
export function signaturesAtMoment(
  doctorId: string,
  allocations: SigAllocation[],
  momentMs: number,
  opts: ConcurrencyOpts,
): number {
  const window = opts.windowMs ?? 60 * 60000;
  let solo = 0;
  const supervisees = new Set<string>();
  for (const r of doctorRoomsFor(doctorId, allocations, opts)) {
    if (!(r.startMs <= momentMs + window && r.endMs > momentMs)) continue;
    if (r.nurseIds.size === 0) solo++;
    else r.nurseIds.forEach(id => supervisees.add(id));
  }
  return solo + supervisees.size;
}

// The doctor's PEAK "at the moment" signature load. Unlike signaturesUsed() — which counts every room the
// doctor touches across the WHOLE DAY — this returns the busiest instant. It excludes rooms later in the day
// that the doctor merely relieves into, matching the Board's "running N rooms at once" placement warning.
export function peakConcurrentSignatures(doctorId: string, allocations: SigAllocation[], opts: ConcurrencyOpts): number {
  const window = opts.windowMs ?? 60 * 60000;
  const roomList = doctorRoomsFor(doctorId, allocations, opts);
  if (roomList.length === 0) return 0;
  // Where the peak actually is. Rearranging the predicate in signaturesAtMoment, a room counts at every moment
  // in [startMs - window, endMs): it JOINS the live set an hour BEFORE it starts (that is what "starting within
  // the hour" means), not when it starts. The count only ever steps UP at those join points, so the peak is
  // attained at one of them — evaluating at r.startMs instead under-reports whenever a short room ends less
  // than an hour before the next one begins (07:00-08:00 + 08:30-16:00 read as 1, but at 07:30 it is 2).
  let peak = 0;
  for (const r of roomList) peak = Math.max(peak, signaturesAtMoment(doctorId, allocations, r.startMs - window, opts));
  return peak;
}
